import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Users, Clock, MessageSquare } from "lucide-react";
import api from "@/lib/api";
import { REVIEW_CRITERIA as CRIT, isChoice } from "@/data/formConfig";
import { choiceLabel, tallyLabel } from "@/i18n";

export default function EvaluatorReviews({ appId }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/applications/${appId}/reviews`).then((r) => setData(r.data)).catch(() => setData({ reviews: [], pending: [], aggregate: null, assigned_count: 0 }));
  }, [appId]);

  if (!data) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 mb-6 flex items-center justify-center h-24">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  }

  if (data.assigned_count === 0 && data.reviews.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 mb-6">
        <h3 className="font-heading text-lg font-medium mb-1 flex items-center gap-2"><Users className="w-5 h-5 text-primary" /> {t("admin.reviews.title")}</h3>
        <p className="text-sm text-muted-foreground">{t("admin.reviews.none_assigned")}</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-6 mb-6" data-testid="evaluator-reviews">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-medium flex items-center gap-2"><Users className="w-5 h-5 text-primary" /> {t("admin.reviews.title")}</h3>
        {data.aggregate && (
          <div className="text-right">
            <span className="font-heading text-2xl font-semibold text-primary">{data.aggregate.overall}</span>
            <span className="text-xs text-muted-foreground ml-2">{t("admin.reviews.avg", { count: data.aggregate.count })}</span>
          </div>
        )}
      </div>

      {data.reviews.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("admin.reviews.none_yet", { count: data.assigned_count })}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted-foreground text-xs uppercase tracking-wider text-left border-b border-border">
                <th className="py-2 pr-4 font-medium">{t("admin.reviews.evaluator")}</th>
                {CRIT.map((c) => <th key={c} className="py-2 px-2 font-medium text-center">{t(`criteria_short.${c}`)}</th>)}
                <th className="py-2 pl-2 font-medium text-center">{t("admin.reviews.overall")}</th>
              </tr>
            </thead>
            <tbody>
              {data.reviews.map((r) => (
                <tr key={r.evaluator_id} className="border-b border-border/60 last:border-0 align-top">
                  <td className="py-3 pr-4">
                    <p className="font-medium">{r.evaluator_name}</p>
                  </td>
                  {CRIT.map((c) => (
                    <td key={c} className="py-3 px-2 text-center text-muted-foreground">
                      {isChoice(c) ? choiceLabel(r.scores[c]) : r.scores[c]}
                    </td>
                  ))}
                  <td className="py-3 pl-2 text-center font-semibold text-primary">{r.overall}</td>
                </tr>
              ))}
              {data.aggregate && (
                <tr className="bg-secondary/50">
                  <td className="py-3 pr-4 font-medium">{t("admin.reviews.average")}</td>
                  {CRIT.map((c) => (
                    <td key={c} className="py-3 px-2 text-center font-medium">
                      {isChoice(c) ? tallyLabel(data.aggregate.scores[c]) : data.aggregate.scores[c]}
                    </td>
                  ))}
                  <td className="py-3 pl-2 text-center font-semibold text-primary">{data.aggregate.overall}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {data.reviews.length > 0 && (
        <div className="mt-6 pt-5 border-t border-border" data-testid="evaluator-comments">
          <h4 className="text-sm font-medium flex items-center gap-2 mb-3">
            <MessageSquare className="w-4 h-4 text-primary" /> {t("admin.reviews.comments")}
          </h4>
          <div className="space-y-3">
            {data.reviews.map((r) => (
              <div key={r.evaluator_id} className="rounded-lg bg-secondary/40 border border-border/60 p-4">
                <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1.5">
                  <p className="text-sm font-medium">{r.evaluator_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {r.overall}/10 · {t(`criteria_short.invited_to_interview`)}: {choiceLabel(r.scores.invited_to_interview)}
                  </p>
                </div>
                {r.comments?.trim()
                  ? <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{r.comments.trim()}</p>
                  : <p className="text-sm text-muted-foreground italic">{t("admin.reviews.no_comment")}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {data.pending?.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mt-4 pt-3 border-t border-border">
          <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> {t("admin.reviews.pending")}</span>
          {data.pending.map((p) => (
            <span key={p.evaluator_id} className="text-xs bg-muted rounded-full px-2.5 py-0.5">{p.evaluator_name}</span>
          ))}
        </div>
      )}
    </div>
  );
}
